import React, { useState, useEffect, useRef } from 'react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { APIGetFuelPolicies } from '../../../config/restAPI/FetchAPI';
import {
    fetchFuelPolicyAttachmentCounts,
    getFuelPolicyAssetsAttachedCount,
    resolveFuelPolicyAssetsAttachedFromApi,
} from './fuelPolicyAssetsAttached';
import {
    buildFetchFuelPoliciesParams,
    levelSourceToDisplayLabel,
    normalizeFuelPolicyFromApi,
    parseFuelPoliciesListResponse,
    resolveFuelPolicyLastEditedAt,
} from './fuelPolicyApiMapping';
import Title from '../../../component/title/Title';
import Table from '../../../component/table/Table';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import BreadCrumbs from '../../../component/breadcrumbs/BreadCrumbs';
import SnackBar from '../../../component/snackbar/Snackbar';
import { CreateWithIcon } from '../../../component/buttonComponent/ButtonComponent';
import FuelConfiguratorTableEmptyState from '../../../component/tableEmptyState/FuelConfiguratorTableEmptyState';
import TruckIcon from '../../../image/truckIcon.svg';
import { useApiData } from '../../../config/reducer/Modules';
import '../geofence/geofence.css';

const seperator = [
    <img loading="lazy" src={TruckIcon} alt="live" style={{ display: 'flex', alignItem: 'center' }} />,
    <div style={{ color: '#475467', fontSize: '14px', fontWeight: 500 }}>Fuel Configurator</div>,
];

const formatTableDate = (ts) => (ts ? {
    date: moment(ts).format('DD MMM YYYY'),
    time: moment(ts).format('hh:mm:ssa [GMT+8]'),
} : null);

const mapPolicyRow = (doc, attachmentCounts, lastEditedOverrides = {}) => {
    const p = normalizeFuelPolicyFromApi(doc);
    if (!p) return null;
    const policyId = p.id;
    const lastTs = lastEditedOverrides[policyId]
        || resolveFuelPolicyLastEditedAt(doc)
        || resolveFuelPolicyLastEditedAt(p);
    const assetsCount = attachmentCounts != null
        ? getFuelPolicyAssetsAttachedCount(attachmentCounts, p.id)
        : resolveFuelPolicyAssetsAttachedFromApi(doc);

    return {
        ...doc,
        ...p,
        id: p.id,
        name: p.name || '—',
        tankCapacityL: p.tankCapacityL != null ? p.tankCapacityL : '—',
        levelSource: levelSourceToDisplayLabel(p.levelSource),
        status: p.status || p.policyStatus || 'DRAFT',
        totalAssets: assetsCount,
        assetsAttached: assetsCount,
        assetsWithOverridesCount: typeof doc.assetsWithOverridesCount === 'number' ? doc.assetsWithOverridesCount : 0,
        dateLastEdited: formatTableDate(lastTs),
    };
};

const FuelConfigurator = () => {
    const { apiResponse } = useApiData();
    const navigate = useNavigate();
    const location = useLocation();
    const token = Cookies.get('jwtToken');
    const agencyID = Cookies.get('agencyUUID');
    const role = Cookies.get('role');

    const [navigtionClick, setNavigtionClick, , setNavigationState] = useOutletContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage] = useState(50);
    const [page, setPage] = useState(1);
    const [emptyState, setEmptyState] = useState(false);
    const [showTable, setShowTable] = useState(true);
    const [triggerSnackbar, settriggerSnackbar] = useState(false);
    const [textTitleSnackbar, setTextTitleSnackbar] = useState(null);
    const [textSubSnackbar, setTextSubSnackbar] = useState(null);
    const [moduleSnackbar, setModuleSnackbar] = useState(null);
    const lastEditedOverridesRef = useRef({});
    const fetchRequestIdRef = useRef(0);

    const inventoryNavigationList = [
        {
            text: 'Assets & Devices', value: 1, path: '/inventory/assetdevices', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner' || apiResponse?.role === 'System Administrator') ? true : apiResponse?.module?.fleetManagement?.asset,
        },
        {
            text: 'Connectivity', value: 4, path: '/inventory/connectivity', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.fleetManagement?.connectivity,
        },
        {
            text: 'Battery Configurator', value: 5, path: '/inventory/batteryconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
        {
            text: 'Vehicle Configurator', value: 6, path: '/inventory/vehicleconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
        {
            text: 'Fuel Price Registry', value: 7, path: '/inventory/priceRegistry', enabled: true,
        },
        {
            text: 'Fuel Configurator', value: 8, path: '/inventory/fuelconfigurator', enabled: apiResponse?.role === 'System Administrator',
        },
    ];

    const fetchFuelPolicies = async (pageNumber) => {
        if (role !== 'System Administrator') return;
        const requestId = fetchRequestIdRef.current + 1;
        fetchRequestIdRef.current = requestId;

        try {
            setLoading(true);
            const params = buildFetchFuelPoliciesParams({ page: pageNumber, perPage });
            const result = await APIGetFuelPolicies(setLoading, token, agencyID, params);
            if (requestId !== fetchRequestIdRef.current) {
                return;
            }

            const { totalData, policies: list } = parseFuelPoliciesListResponse(result);
            const total = totalData ?? list.length;

            const mapRows = (attachmentCounts) => list.map((doc) => mapPolicyRow(
                doc,
                attachmentCounts,
                lastEditedOverridesRef.current,
            )).filter(Boolean);

            if (list.length > 0) {
                setShowTable(true);
                setEmptyState(false);
                setRows(mapRows(null));
                setTotalRows(total);

                fetchFuelPolicyAttachmentCounts(token, agencyID).then((attachmentCounts) => {
                    if (requestId !== fetchRequestIdRef.current || attachmentCounts == null) {
                        return;
                    }
                    setRows(mapRows(attachmentCounts));
                });
            } else {
                setShowTable(false);
                setEmptyState(true);
                setRows([]);
                setTotalRows(0);
            }
        } catch (e) {
            if (requestId !== fetchRequestIdRef.current) {
                return;
            }
            const apiMessage = e?.response?.data?.error?.message;
            setShowTable(false);
            setEmptyState(false);
            setRows([]);
            setTotalRows(0);
            settriggerSnackbar(true);
            setTextTitleSnackbar('Error');
            setTextSubSnackbar(apiMessage || 'Unable to load fuel policies. Please try again later.');
            setModuleSnackbar('error');
        } finally {
            if (requestId === fetchRequestIdRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (location.state?.snackbarStatus) {
            settriggerSnackbar(true);
            setTextTitleSnackbar(location.state.snackbarTitle);
            setTextSubSnackbar(location.state.snackbarSub);
            setModuleSnackbar(location.state.snackbarStatus);
            if (location.state.fuelPolicyLastEdited?.id && location.state.fuelPolicyLastEdited?.lastEditedAt) {
                const { id, lastEditedAt } = location.state.fuelPolicyLastEdited;
                lastEditedOverridesRef.current = {
                    ...lastEditedOverridesRef.current,
                    [id]: lastEditedAt,
                };
            }
            fetchFuelPolicies(page);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    useEffect(() => {
        setNavigtionClick(8);
        setNavigationState('inventory');
        if (token && role === 'System Administrator') {
            fetchFuelPolicies(page);
        }
        return () => {
            fetchRequestIdRef.current += 1;
        };
    }, [token, role]);

    const handlePageChange = (p) => {
        setPage(p);
        fetchFuelPolicies(p);
    };

    const handleAdd = () => {
        navigate('/inventory/fuelconfigurator/addFuelConfig', { replace: true });
    };

    return (
        <div className="inventory-container">
            <ListNavigationTab list={inventoryNavigationList} navigtionClick={navigtionClick} setNavigtionClick={setNavigtionClick} />
            <div className="inventory-header">
                <BreadCrumbs customSeperator={seperator} />
                <Title
                  title="Fuel Configurator"
                  subtitle="This module is for configuring fuel policy parameters that can be applied to different asset types."
                  titleSize="1.875rem"
                />
            </div>
            <div className="inventory-body">
                <div className="inventory-body-filter">
                    <div className="inventory-filter-button">
                        <CreateWithIcon label="Create Policy" onClickFunction={handleAdd} buttonWidth="9.75rem" buttonHeight="2.75rem" module="policy" />
                    </div>
                </div>
                {showTable && (
                    <div className={loading ? 'inventory-body-text-loading' : 'inventory-body-text'}>
                        <Table
                          tableData={rows}
                          pagination
                          module="fuelConfigurator"
                          handlePageChange={handlePageChange}
                          loading={loading}
                          totalRows={totalRows}
                          backgroundColor="#F9FAFB"
                          paginationPerPage={perPage}
                          paginationDefaultPage={page}
                        />
                    </div>
                )}
                {emptyState && (
                    <div className="inventory-body-empty">
                        <FuelConfiguratorTableEmptyState />
                    </div>
                )}
            </div>
            {triggerSnackbar && (
                <div className="geofence-snackbar-container">
                    <SnackBar state={triggerSnackbar} setState={settriggerSnackbar} title={textTitleSnackbar} subtitile={textSubSnackbar} module={moduleSnackbar} action={moduleSnackbar} />
                </div>
            )}
        </div>
    );
};

export default FuelConfigurator;
